<template>
  <div class="ttfb-mini-chart-container">
    <div class="ttfb-chart">
      <hover-tooltip
        v-for="(point, index) in chartData"
        :key="`bar-${index}`"
        :tooltip-style="tooltipStyle"
        :offset="-50"
        class="chart-bar-wrapper"
      >
        <div class="chart-bar" :style="getBarStyle(point)"></div>
        <template v-slot:tooltip>
          <div class="font-weight-bold">{{ point.ttfb_ms }}ms</div>
          <div class="text-caption">{{ formatTooltipDate(point.date) }}</div>
          <div class="text-caption">Status: {{ point.status_code || 200 }}</div>
        </template>
      </hover-tooltip>
      <div v-if="chartData.length === 0" class="empty-chart">
        <span class="text-caption text--secondary">No data</span>
      </div>
    </div>
  </div>
</template>

<script>
import HoverTooltip from "@/components/common/HoverTooltip.vue";

export default {
  name: "TtfbMiniChart",
  components: {
    HoverTooltip,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      tooltipStyle: {
        backgroundColor: "#fff",
        color: "#333",
        padding: "8px 12px",
        borderRadius: "4px",
        fontSize: "12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
        minWidth: "120px",
      },
    };
  },
  computed: {
    chartData() {
      if (!this.data || this.data.length === 0) {
        return [];
      }

      return this.data.slice(-40).reverse();
    },
    valueRange() {
      if (this.chartData.length === 0) {
        return { min: 0, max: 1, range: 1 };
      }
      const values = this.chartData.map((d) => d.ttfb_ms);
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      return {
        min: minVal,
        max: maxVal,
        range: maxVal - minVal || 1,
      };
    },
  },
  methods: {
    getBarStyle(point) {
      // Calculate height based on value
      const normalizedHeight =
        (point.ttfb_ms - this.valueRange.min) / this.valueRange.range;
      const height = Math.max(2, normalizedHeight * 25 + 2);

      const statusCode = point.status_code || point.last_status_code || 200;
      let backgroundColor;
      if (statusCode !== 200) {
        backgroundColor = "#f44336"; // red - HTTP code ≠ 200
      } else if (point.ttfb_ms > 800) {
        backgroundColor = "#ff9800"; // orange - TTFB > 800ms
      } else {
        backgroundColor = "#4caf50"; // green - all good
      }

      const barWidth = Math.floor(98 / this.chartData.length);

      return {
        height: `${height}px`,
        backgroundColor,
        width: `${barWidth}px`,
        marginRight: "1px",
      };
    },
    formatTooltipDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.ttfb-mini-chart-container {
  position: relative;
}

.ttfb-chart {
  width: 100px;
  height: 30px;
  position: relative;
  padding: 1px;
  display: flex;
  align-items: flex-end;
}

.chart-bar-wrapper {
  display: inline-flex !important;
  align-items: flex-end;
  height: 100%;
}

.chart-bar {
  cursor: pointer;
  transition: opacity 0.2s;
  border-radius: 1px;
}

.chart-bar:hover {
  opacity: 0.8;
}

.empty-chart {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #999;
  font-size: 11px;
}
</style>
